import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CodeIcon from '@mui/icons-material/Code';
import { useNavigate } from 'react-router-dom';
import {
  statusLabel,
  priorityLabel,
  TICKET_STATUSES,
  PRIORITIES,
} from '../../../constants/tickets';
import { dashboardTicketPath } from '../../../lib/dashboardRoutes';
import {
  ticketListActivityMeta,
  ticketListPreviewText,
} from '../../../lib/ticketCommentMeta';

const ticketTableShellSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  height: '100%',
  width: '100%',
  overflow: 'hidden',
};

const ticketTableContainerSx = {
  flex: 1,
  minHeight: 0,
  width: '100%',
  overflow: 'auto',
};

const ticketCompactCellSx = {
  whiteSpace: 'nowrap',
  width: '1%',
};

const ticketWrapCellSx = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const ticketTitleCellSx = {
  ...ticketWrapCellSx,
  width: '28%',
  minWidth: 160,
  maxWidth: 420,
};

const ticketAssigneeCellSx = {
  ...ticketWrapCellSx,
  minWidth: 120,
  maxWidth: 200,
};

const ticketLabelsCellSx = {
  ...ticketWrapCellSx,
  minWidth: 100,
  maxWidth: 180,
};

const ticketTextClamp2Sx = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
};

const ticketTextClamp3Sx = {
  ...ticketTextClamp2Sx,
  WebkitLineClamp: 3,
};

const ticketStickyActionHeadSx = {
  position: 'sticky',
  right: 0,
  backgroundColor: 'background.paper',
  zIndex: 4,
  borderLeft: '1px solid',
  borderColor: 'divider',
};

const ticketStickyActionBodySx = {
  position: 'sticky',
  right: 0,
  backgroundColor: 'background.paper',
  zIndex: 1,
  borderLeft: '1px solid',
  borderColor: 'divider',
  'tr.Mui-selected > &': {
    backgroundColor: 'action.selected',
  },
  'tr:hover > &': {
    backgroundColor: 'action.hover',
  },
};

/** Human-readable column titles used in the filter/sort popover. */
const COLUMN_TITLES = {
  title: 'Title',
  assignee: 'Assignee',
  status: 'Status',
  priority: 'Priority',
  label: 'Labels',
  updated: 'Updated',
};

/** Per-column order button labels (asc/desc). */
const COLUMN_ORDER_LABELS = {
  title: { asc: 'A → Z', desc: 'Z → A' },
  assignee: { asc: 'A → Z', desc: 'Z → A' },
  label: { asc: 'A → Z', desc: 'Z → A' },
  status: { asc: 'Ascending', desc: 'Descending' },
  priority: { asc: 'Low → High', desc: 'High → Low' },
  updated: { asc: 'Oldest first', desc: 'Newest first' },
};

const statusSortIndex = (s) => {
  const i = TICKET_STATUSES.findIndex((x) => x.id === s);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
};

const prioritySortIndex = (p) => {
  const i = PRIORITIES.findIndex((x) => x.id === p);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
};

const tsToMillis = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') {
    const d = ts.toDate();
    return d ? d.getTime() : 0;
  }
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  const parsed = new Date(ts).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export default function TicketTable({
  canWriteTicketing,
  loading,
  displayedTickets,
  tableColSpan,
  canModifyTicket,
  canRemoveTicket,
  selectedIds,
  bulkUpdating,
  someSelectableSelected,
  allSelectableSelected,
  selectableTicketsCount,
  onToggleSelectAll,
  onToggleSelectOne,
  onOpenEdit,
  activeTicketId = null,
  compact = false,
  labelMap,
  userByUid,
  agentById,
  formatDateTime,
  ticketSandboxMetaById,
  onViewCodePatches,
  assigneeTableLabel,
  showAiActivityOnly,
  showResolved,
  archivedView,
  onDeleteTicketConfirm,
  onSetArchived,
}) {
  const navigate = useNavigate();

  const EMPTY_FILTERS = { title: '', assignee: '', status: '', priority: '', label: '' };
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  const setFilter = (key) => (e) =>
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  const clearFilters = () => setFilters(EMPTY_FILTERS);

  // Sorting is tracked separately so the filtering logic stays untouched.
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Popover state: which column's filter/sort menu is open and its anchor.
  const [activeFilterMenu, setActiveFilterMenu] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const openFilterMenu = (key, event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setActiveFilterMenu(key);
  };
  const closeFilterMenu = () => {
    setActiveFilterMenu(null);
    setMenuAnchorEl(null);
  };

  const setSort = (key, direction) =>
    setSortConfig((prev) =>
      prev.key === key && prev.direction === direction
        ? { key: null, direction: 'asc' }
        : { key, direction },
    );

  const isColumnActive = (key) =>
    Boolean(filters[key]) || sortConfig.key === key;

  const clearColumn = (key) => {
    setFilters((prev) => (key in prev ? { ...prev, [key]: '' } : prev));
    setSortConfig((prev) => (prev.key === key ? { key: null, direction: 'asc' } : prev));
  };

  const filteredTickets = useMemo(() => {
    const base = displayedTickets || [];
    if (!hasActiveFilters) return base;
    const title = filters.title.trim().toLowerCase();
    const assignee = filters.assignee.trim().toLowerCase();
    const label = filters.label.trim().toLowerCase();
    return base.filter((t) => {
      if (title && !(t.title || '').toLowerCase().includes(title)) return false;
      if (assignee) {
        const label2 = (assigneeTableLabel(t) || '').toLowerCase();
        if (!label2.includes(assignee)) return false;
      }
      if (filters.status && t.status !== filters.status) return false;
      if (filters.priority && t.priority !== filters.priority) return false;
      if (label) {
        const names = (t.labelIds || []).map((lid) =>
          (labelMap[lid]?.name || lid || '').toLowerCase(),
        );
        if (!names.some((n) => n.includes(label))) return false;
      }
      return true;
    });
  }, [displayedTickets, filters, hasActiveFilters, assigneeTableLabel, labelMap]);

  const sortedTickets = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) return filteredTickets;
    const dir = direction === 'desc' ? -1 : 1;
    const firstLabelName = (t) => {
      const lid = (t.labelIds || [])[0];
      if (!lid) return '';
      return (labelMap[lid]?.name || lid || '').toLowerCase();
    };
    const rows = [...filteredTickets];
    rows.sort((a, b) => {
      let c = 0;
      if (key === 'title') c = (a.title || '').localeCompare(b.title || '');
      else if (key === 'assignee') {
        c = (assigneeTableLabel(a) || '').localeCompare(assigneeTableLabel(b) || '');
      } else if (key === 'label') c = firstLabelName(a).localeCompare(firstLabelName(b));
      else if (key === 'status') c = statusSortIndex(a.status) - statusSortIndex(b.status);
      else if (key === 'priority') {
        c = prioritySortIndex(a.priority) - prioritySortIndex(b.priority);
      } else if (key === 'updated') c = tsToMillis(a.updatedAt) - tsToMillis(b.updatedAt);
      return dir * c;
    });
    return rows;
  }, [filteredTickets, sortConfig, assigneeTableLabel, labelMap]);

  const anySortOrFilter = hasActiveFilters || Boolean(sortConfig.key);

  const renderHeaderCell = (key, cellSx) => {
    const active = isColumnActive(key);
    return (
      <TableCell sx={cellSx}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {COLUMN_TITLES[key]}
          </Box>
          <Tooltip title={active ? 'Filter / sort active' : 'Filter / sort'}>
            <IconButton
              size="small"
              onClick={(e) => openFilterMenu(key, e)}
              aria-label={`Filter or sort ${COLUMN_TITLES[key]}`}
              sx={{
                p: 0.25,
                color: active ? 'primary.main' : 'text.disabled',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {active ? (
                <FilterAltIcon sx={{ fontSize: 16 }} />
              ) : (
                <FilterListIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    );
  };

  const renderFilterMenuContent = (key) => {
    if (!key) return null;
    const order = COLUMN_ORDER_LABELS[key];
    return (
      <Stack spacing={1.5} sx={{ width: 240 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {COLUMN_TITLES[key]}
        </Typography>

        {(key === 'title' || key === 'assignee' || key === 'label') && (
          <TextField
            autoFocus
            value={filters[key]}
            onChange={setFilter(key)}
            size="small"
            fullWidth
            placeholder={`Filter ${COLUMN_TITLES[key].toLowerCase()}`}
          />
        )}
        {key === 'status' && (
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={setFilter('status')}
            size="small"
            fullWidth
          >
            <MenuItem value="">All statuses</MenuItem>
            {TICKET_STATUSES.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {key === 'priority' && (
          <TextField
            select
            label="Priority"
            value={filters.priority}
            onChange={setFilter('priority')}
            size="small"
            fullWidth
          >
            <MenuItem value="">All priorities</MenuItem>
            {PRIORITIES.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {order && (
          <>
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Order
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                fullWidth
                variant={
                  sortConfig.key === key && sortConfig.direction === 'asc'
                    ? 'contained'
                    : 'outlined'
                }
                startIcon={<ArrowUpwardIcon fontSize="small" />}
                onClick={() => setSort(key, 'asc')}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {order.asc}
              </Button>
              <Button
                size="small"
                fullWidth
                variant={
                  sortConfig.key === key && sortConfig.direction === 'desc'
                    ? 'contained'
                    : 'outlined'
                }
                startIcon={<ArrowDownwardIcon fontSize="small" />}
                onClick={() => setSort(key, 'desc')}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {order.desc}
              </Button>
            </Stack>
          </>
        )}

        <Button
          size="small"
          color="inherit"
          onClick={() => clearColumn(key)}
          disabled={!isColumnActive(key)}
          sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
        >
          Clear this column
        </Button>
      </Stack>
    );
  };

  return (
    <Box sx={ticketTableShellSx}>
      <TableContainer sx={ticketTableContainerSx}>
        <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {canWriteTicketing && (
                <TableCell padding="checkbox" sx={{ width: 48, ...ticketCompactCellSx }}>
                  <Checkbox
                    indeterminate={someSelectableSelected}
                    checked={allSelectableSelected}
                    disabled={selectableTicketsCount === 0 || bulkUpdating}
                    onChange={onToggleSelectAll}
                    inputProps={{ 'aria-label': 'Select all tickets' }}
                  />
                </TableCell>
              )}
              {renderHeaderCell('title', ticketTitleCellSx)}
              {renderHeaderCell('assignee', ticketAssigneeCellSx)}
              {renderHeaderCell('status', ticketCompactCellSx)}
              {renderHeaderCell('priority', ticketCompactCellSx)}
              {!compact && renderHeaderCell('label', ticketLabelsCellSx)}
              {!compact && renderHeaderCell('updated', ticketCompactCellSx)}
              {canWriteTicketing && (
                <TableCell align="right" sx={ticketStickyActionHeadSx}>
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                    <span>Actions</span>
                    {anySortOrFilter && (
                      <Tooltip title="Clear all filters & sorting">
                        <IconButton
                          size="small"
                          onClick={() => {
                            clearFilters();
                            setSortConfig({ key: null, direction: 'asc' });
                          }}
                          aria-label="Clear all filters and sorting"
                          sx={{ p: 0.25, color: 'primary.main' }}
                        >
                          <FilterAltOffIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={tableColSpan}>Loading…</TableCell>
              </TableRow>
            )}
            {!loading && sortedTickets.length === 0 && (
              <TableRow>
                <TableCell colSpan={tableColSpan}>
                  <Typography variant="body2" color="text.secondary">
                    {hasActiveFilters
                      ? 'No tickets match the current column filters.'
                      : showAiActivityOnly
                        ? 'No AI-delegated or agent-assigned tickets in this view.'
                        : !showResolved
                          ? 'No active tickets (resolved tickets are hidden).'
                          : archivedView
                            ? 'No archived tickets.'
                            : 'No active tickets yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && sortedTickets.map((t) => {
              const preview = ticketListPreviewText(t);
              const activityMeta = ticketListActivityMeta(
                t,
                userByUid,
                agentById,
                formatDateTime,
              );
              const sandboxMeta = ticketSandboxMetaById[t.id];
              return (
                <TableRow
                  key={t.id}
                  hover
                  onClick={() => onOpenEdit(t)}
                  sx={{
                    cursor: 'pointer',
                    ...(t.id === activeTicketId
                      ? {
                          bgcolor: 'action.selected',
                          '&:hover': { bgcolor: 'action.selected' },
                        }
                      : {}),
                  }}
                  selected={Boolean(selectedIds[t.id]) || t.id === activeTicketId}
                >
                  {canWriteTicketing && (
                    <TableCell padding="checkbox" sx={{ width: 48, ...ticketCompactCellSx }} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={Boolean(selectedIds[t.id])}
                        disabled={!canModifyTicket(t) || bulkUpdating}
                        onChange={() => onToggleSelectOne(t.id)}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ 'aria-label': `Select ticket ${t.title || t.id}` }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={ticketTitleCellSx}>
                    <Typography
                      variant="subtitle2"
                      title={t.title}
                      sx={ticketTextClamp2Sx}
                    >
                      {t.title}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={0.75}
                      sx={{ mt: 0.25, flexWrap: 'wrap' }}
                    >
                      {((typeof t.urgencyScore === 'number' && t.urgencyScore > 7) ||
                        t.urgency === 'High' ||
                        t.urgency === 'Critical') && (
                        <Chip
                          size="small"
                          label={t.urgency === 'Critical' ? 'Critical' : 'High Priority'}
                          color="error"
                          variant="filled"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {t.aiEnriched && t.category && (
                        <Chip
                          size="small"
                          label={t.category}
                          variant="outlined"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {t.source === 'ai_agent_delegation' && (
                        <Chip
                          size="small"
                          label="AI delegated"
                          color="secondary"
                          variant="outlined"
                          icon={<SmartToyOutlinedIcon />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {t.parentTicketId && (
                        <Chip
                          size="small"
                          label={`From ticket ${t.parentTicketId.slice(0, 8)}…`}
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(dashboardTicketPath(t.parentTicketId));
                          }}
                        />
                      )}
                      {sandboxMeta?.total > 0 && (
                        <Chip
                          size="small"
                          label={
                            sandboxMeta.pending > 0
                              ? `${sandboxMeta.pending} patch${sandboxMeta.pending === 1 ? '' : 'es'} pending`
                              : `${sandboxMeta.total} code patch${sandboxMeta.total === 1 ? '' : 'es'}`
                          }
                          variant="outlined"
                          icon={<CodeIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewCodePatches(t.id);
                          }}
                        />
                      )}
                    </Stack>
                    {preview && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        title={preview}
                        sx={{ ...ticketTextClamp3Sx, mt: 0.25 }}
                      >
                        {preview}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      display="block"
                      title={activityMeta}
                      sx={{ ...ticketTextClamp2Sx, mt: 0.25 }}
                    >
                      {activityMeta}
                    </Typography>
                  </TableCell>
                  <TableCell sx={ticketAssigneeCellSx}>
                    {t.isAssignedToAi && t.assigneeAgentId ? (
                      <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
                        <SmartToyOutlinedIcon
                          fontSize="small"
                          color="secondary"
                          sx={{ flexShrink: 0 }}
                        />
                        <Typography variant="body2" title={assigneeTableLabel(t)} sx={ticketWrapCellSx}>
                          {assigneeTableLabel(t)}
                        </Typography>
                        <Chip
                          label="AI"
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 20, flexShrink: 0 }}
                        />
                      </Stack>
                    ) : (
                      <Typography variant="body2" title={assigneeTableLabel(t)} sx={ticketWrapCellSx}>
                        {assigneeTableLabel(t)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={ticketCompactCellSx}>
                    <Chip size="small" label={statusLabel(t.status)} variant="outlined" />
                  </TableCell>
                  <TableCell sx={ticketCompactCellSx}>{priorityLabel(t.priority)}</TableCell>
                  {!compact && (
                    <TableCell sx={ticketLabelsCellSx}>
                      <Stack direction="row" gap={0.5} sx={{ flexWrap: 'wrap' }}>
                        {(t.labelIds || []).map((lid) => (
                          <Chip
                            key={lid}
                            size="small"
                            label={labelMap[lid]?.name || lid}
                            sx={{ bgcolor: labelMap[lid]?.color || 'grey.300', color: '#fff' }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                  )}
                  {!compact && (
                    <TableCell sx={ticketCompactCellSx}>{formatDateTime(t.updatedAt)}</TableCell>
                  )}
                  {canWriteTicketing && (
                    <TableCell align="right" sx={ticketStickyActionBodySx}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {sandboxMeta?.total > 0 && (
                          <Tooltip title="View code patches">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewCodePatches(t.id);
                              }}
                            >
                              <CodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canModifyTicket(t) && (
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenEdit(t); }} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canModifyTicket(t) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSetArchived(t, !t.archived);
                            }}
                            title={t.archived ? 'Restore' : 'Archive'}
                          >
                            {t.archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveOutlinedIcon fontSize="small" />}
                          </IconButton>
                        )}
                        {canRemoveTicket(t) && (
                          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDeleteTicketConfirm(t); }} title="Delete">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(activeFilterMenu) && Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={closeFilterMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, mt: 0.5, borderRadius: 2 } } }}
      >
        {renderFilterMenuContent(activeFilterMenu)}
      </Popover>
    </Box>
  );
}
